import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  Briefcase, 
  LayoutDashboard, 
  CalendarDays, 
  CalendarClock, 
  ListChecks, 
  Users, 
  Shield,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  Key,
  AlertCircle,
  CheckCircle
} from 'lucide-react';

const Layout = ({ children, currentView, onNavigate }) => {
  const { user, logout, isAdmin } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const navItems = [
    { id: 'dashboard', label: 'Painel Geral', icon: LayoutDashboard, adminOnly: true },
    { id: 'calendar', label: 'Cronograma', icon: CalendarDays },
    { id: 'requests', label: 'Disponibilidade', icon: CalendarClock },
    { id: 'entries', label: 'Histórico', icon: ListChecks },
    { id: 'settings', label: 'Parceiros', icon: Users, adminOnly: true }
  ];

  const getRoleLabel = () => {
    return isAdmin() ? 'Gestor' : 'Parceiro';
  };

  const handleNavClick = (viewId) => {
    onNavigate(viewId);
    setMobileMenuOpen(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword !== confirmPassword) {
      setPasswordError('As senhas não coincidem');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row overflow-hidden bg-slate-100">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 bg-slate-900 text-white">
        <div className="flex items-center gap-3">
          <Briefcase className="h-5 w-5 text-indigo-400" />
          <h1 className="text-lg font-heading font-bold">Portal de Parceiros</h1>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          className="text-white"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 bg-slate-900 z-50 shadow-lg">
          <nav className="p-4 space-y-2">
            {navItems.map(item => {
              if (item.adminOnly && !isAdmin()) return null;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    currentView === item.id 
                      ? 'bg-slate-800 text-white' 
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {/* Sidebar - Desktop */}
      <aside className={`hidden md:flex flex-col bg-slate-900 text-white transition-all duration-300 ${
        collapsed ? 'w-20' : 'w-64'
      }`}>
        <div className={`p-6 flex items-center gap-3 border-b border-slate-800 ${collapsed ? 'justify-center' : ''}`}>
          <Briefcase className="h-5 w-5 text-indigo-400 flex-shrink-0" />
          {!collapsed && <h1 className="text-lg font-heading font-bold tracking-wide">Portal de Parceiros</h1>}
        </div>
        
        <nav className="mt-6 px-4 space-y-2 flex-1">
          {navItems.map(item => {
            if (item.adminOnly && !isAdmin()) return null;
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  currentView === item.id 
                    ? 'bg-slate-800 text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                } ${collapsed ? 'justify-center' : ''}`}
                data-testid={`nav-${item.id}`}
                title={collapsed ? item.label : ''}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom Section */}
        <div className="p-4 border-t border-slate-800 space-y-4">
          {/* User Info */}
          {!collapsed && (
            <div className="bg-slate-800 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase">Conectado como</span>
                {isAdmin() && <Shield className="h-4 w-4 text-indigo-400" />}
              </div>
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              <span className={`text-xs px-2 py-0.5 rounded mt-2 inline-block ${
                isAdmin() ? 'bg-indigo-600 text-white' : 'bg-slate-700 text-slate-300'
              }`}>
                {getRoleLabel()}
              </span>
            </div>
          )}

          {/* Change Password Button */}
          <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
            <DialogTrigger asChild>
              <Button 
                variant="ghost" 
                className={`w-full text-slate-400 hover:text-white hover:bg-slate-800 ${collapsed ? 'px-2' : ''}`}
                data-testid="change-password-btn"
              >
                <Key className="h-4 w-4" />
                {!collapsed && <span className="ml-2">Alterar Senha</span>}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Alterar Senha</DialogTitle>
                <DialogDescription>
                  Digite sua senha atual e escolha uma nova senha.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleChangePassword} className="space-y-4 mt-4">
                {passwordError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {passwordError}
                  </div>
                )}
                {passwordSuccess && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4" />
                    Senha alterada com sucesso!
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="current-password">Senha Atual</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    data-testid="current-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nova Senha</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar Nova Senha</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowPasswordModal(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={changingPassword}>
                    {changingPassword ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Logout Button */}
          <Button 
            variant="ghost" 
            className={`w-full text-slate-400 hover:text-white hover:bg-slate-800 ${collapsed ? 'px-2' : ''}`}
            onClick={logout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
            {!collapsed && <span className="ml-2">Sair</span>}
          </Button>

          {/* Collapse Toggle */}
          <Button 
            variant="ghost" 
            size="sm"
            className="w-full text-slate-500 hover:text-white hover:bg-slate-800"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-slate-100 p-4 md:p-8 custom-scroll">
        {children}
      </main>
    </div>
  );
};

export default Layout;
